/**
 * @fileoverview A week of teaching, three ways — the org portal's calendar.
 *
 * Mirrors the instructor calendar's shape (list / week / month over one
 * payload, one range control) so the two portals read as one product. What it
 * does NOT mirror is the instructor's assumption that a day holds a handful of
 * lessons in a row. An organization's Tuesday is twelve instructors teaching
 * nine classes, half of them at four o'clock — so the week is a real time grid
 * with overlap resolved into lanes, not a stack of rows that silently hides
 * everything after the first.
 *
 * **Colour carries the program, never the meaning.** Cancelled and covered are
 * said in words, because a calendar that encodes state in hue alone is
 * unreadable to a colourblind administrator and unprintable for everyone.
 *
 * **A mirror, not an editor.** Nothing here writes; meetings are built by the
 * class's schedule and owned by Scheduler. Pressing one opens what it belongs
 * to, which is the caller's decision, not this component's.
 *
 * Events are normalized by the caller into
 * `{ id, date, time_local, timezone?, duration_minutes, title, subtitle, color,
 *    muted, tags, onPress }`, so the org-wide calendar and one class's own
 * calendar share every pixel of this and disagree only about what they put in.
 *
 * **Two list styles, one component (decision D2).** The org-wide list is
 * `grouped`: one week at a time under day headings, which is what keeps a
 * Tuesday with six overlapping classes readable. One class's calendar is a
 * term — a single sequence — so it asks for `listStyle="flat"`: every meeting
 * in one card, one row each, the date on the right, no day headings and no
 * week to page through.
 *
 * `timezone` is the zone the meeting's own time is stated in, and it is
 * rendered wherever there is room for it. A connected class belongs to the
 * business that schedules it, whose zone is not always the administrator's —
 * an unqualified "4:00" on their screen would be read as four o'clock where
 * they are sitting.
 */

import { useMemo } from "react";
import { Card, Icon, SegmentedControl, Tag } from "@/components/studio/primitives";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * The colour keys the server cycles (`services/org_calendar.COLOURS`) as
 * semantic tokens, so both themes resolve them: per program on the org
 * Calendar, per class on a viewer's.
 */
export const SWATCH = {
  violet: "var(--cal-violet)",
  teal: "var(--cal-teal)",
  amber: "var(--cal-amber)",
  rose: "var(--cal-rose)",
  sky: "var(--cal-sky)",
  lime: "var(--cal-lime)",
  indigo: "var(--cal-indigo)",
  orange: "var(--cal-orange)",
};

export const VIEWS = [
  { id: "list", label: "List", testId: "cal-view-list" },
  { id: "week", label: "Week", testId: "cal-view-week" },
  { id: "month", label: "Month", testId: "cal-view-month" },
];

/**
 * `d` moved by whole calendar days, at its local time of day. Never by
 * milliseconds: a day with a clock change is 23 or 25 hours long, and adding
 * 24-hour days from a midnight lands on the evening before once one is
 * crossed, which once repeated Nov 1 in October's grid and kept "next week"
 * on the week of Oct 26.
 */
export function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

/** Local YYYY-MM-DD. */
export const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Monday of the week containing `d`. */
export function weekStart(d) {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return copy;
}

/** Monday of the week containing the 1st — the top-left cell of a month grid. */
export function monthGridStart(d) {
  return weekStart(new Date(d.getFullYear(), d.getMonth(), 1));
}

/** The range a view asks the server for. Month asks wide enough to fill its
    grid, which spills into the neighbouring months by design. */
export function rangeFor(view, anchor) {
  if (view === "month") {
    const first = monthGridStart(anchor);
    return { start: iso(first), end: iso(addDays(first, 41)) };
  }
  const first = weekStart(anchor);
  return { start: iso(first), end: iso(addDays(first, 6)) };
}

const fromIso = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const minutes = (timeLocal) => {
  const [h, m] = String(timeLocal || "").split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : 0;
};

const clock = (timeLocal) => {
  const [h, m] = String(timeLocal || "").split(":").map(Number);
  if (!Number.isFinite(h)) return "";
  const ampm = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${ampm}`;
};

/** The week grid's scale, and the shortest a block is drawn. A meeting
    shorter than that still takes that much of the column, so the layout
    treats it as that long when it decides what sits beside what. */
const ROW = 52; // px per hour
const MIN_BLOCK = 26; // px
const MIN_BLOCK_MINUTES = (MIN_BLOCK / ROW) * 60;

/**
 * Lay one day's meetings out side by side where they overlap, and full width
 * where they don't.
 *
 * The day splits into clusters — runs of meetings that overlap one another,
 * directly or through a third. Each cluster is packed on its own with the
 * classic interval-colouring sweep (a meeting takes the first lane free at its
 * start), so three classes at four o'clock are three readable thirds. Lanes are
 * counted PER CLUSTER: counting them across the whole day squeezed a 9:40 class
 * with nothing beside it into a quarter of the column because four classes
 * overlapped at 3:15. Finally a meeting widens into the lanes to its right for
 * as long as nothing in them overlaps it.
 *
 * Returns `{ event, start, end, lane, span, lanes }` per meeting: it sits at
 * `lane / lanes` of the column and is `span / lanes` wide.
 */
export function layoutDay(events) {
  const items = events
    .map((event) => {
      const start = minutes(event.time_local);
      const end = start + (event.duration_minutes || 45);
      return { event, start, end, drawnEnd: Math.max(end, start + MIN_BLOCK_MINUTES) };
    })
    .sort((a, b) => a.start - b.start || b.drawnEnd - a.drawnEnd);

  const clusters = [];
  let reach = -Infinity;
  for (const item of items) {
    if (item.start >= reach) clusters.push([]);
    reach = item.start >= reach ? item.drawnEnd : Math.max(reach, item.drawnEnd);
    clusters.at(-1).push(item);
  }

  const overlaps = (a, b) => a.start < b.drawnEnd && b.start < a.drawnEnd;
  for (const cluster of clusters) {
    const laneEnds = [];
    for (const item of cluster) {
      let lane = laneEnds.findIndex((laneEnd) => laneEnd <= item.start);
      if (lane === -1) lane = laneEnds.push(0) - 1;
      laneEnds[lane] = item.drawnEnd;
      item.lane = lane;
    }
    for (const item of cluster) {
      item.lanes = laneEnds.length;
      item.span = 1;
      while (
        item.lane + item.span < item.lanes &&
        !cluster.some((other) => other.lane === item.lane + item.span && overlaps(item, other))
      ) {
        item.span += 1;
      }
    }
  }
  return items.map(({ drawnEnd, ...placed }) => placed);
}

/* --------------------------------------------------------------- the views */

function EventChip({ event, compact }) {
  const body = (
    <>
      <span className="flex items-center gap-1.5">
        <span
          className="h-full min-h-[10px] w-[3px] shrink-0 rounded-full"
          style={{ backgroundColor: event.color || "var(--accent-yellow)" }}
        />
        <span className="truncate font-mono text-[10px] text-[var(--text-tertiary)]">
          {clock(event.time_local).replace(/\s?[AP]M/, "")}
        </span>
      </span>
      <span className={`block truncate font-sans ${compact ? "text-[11px]" : "text-[12px]"} text-[var(--text-primary)]`}>
        {event.title}
      </span>
      {!compact && (event.subtitle || event.timezone) && (
        <span className="block truncate font-sans text-[11px] text-[var(--text-tertiary)]">
          {[event.timezone, event.subtitle].filter(Boolean).join(" · ")}
        </span>
      )}
    </>
  );
  return (
    <button
      type="button"
      onClick={event.onPress}
      disabled={!event.onPress}
      data-testid={`cal-event-${event.id}`}
      className={[
        "w-full overflow-hidden rounded-[8px] border px-1.5 py-1 text-left transition-colors",
        event.muted
          ? "border-[var(--studio-divider)] bg-transparent opacity-60"
          : "border-[var(--studio-divider)] bg-[var(--studio-raised)] hover:bg-[var(--studio-overlay)]",
      ].join(" ")}
    >
      {body}
    </button>
  );
}

function ListView({ days, emptyText }) {
  return (
    <div className="flex flex-col gap-3">
      {days.map((day) => (
        <Card key={day.key} padding={0} data-testid={`cal-day-${day.key}`}>
          <div className="border-b border-[var(--studio-divider)] px-[18px] py-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
            {fromIso(day.key).toLocaleDateString(undefined, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </div>
          {day.events.length === 0 ? (
            <div className="px-[18px] py-3 font-sans text-[13px] text-[var(--text-tertiary)]">{emptyText}</div>
          ) : (
            day.events.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={e.onPress}
                disabled={!e.onPress}
                data-testid={`cal-event-${e.id}`}
                className="flex w-full items-center gap-3 border-b border-[var(--studio-divider)] px-[18px] py-2.5 text-left last:border-b-0 enabled:hover:bg-[var(--studio-interactive)]"
              >
                <span
                  className="h-8 w-1 shrink-0 rounded-full"
                  style={{ backgroundColor: e.color || "var(--accent-yellow)" }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-sans text-[15px] text-[var(--text-primary)]">{e.title}</span>
                  <span className="block truncate font-sans text-[13px] text-[var(--text-tertiary)]">
                    {[[clock(e.time_local), e.timezone].filter(Boolean).join(" "), e.subtitle]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                {(e.tags || []).map((t) => (
                  <Tag key={t.label} accent={t.accent}>
                    {t.label}
                  </Tag>
                ))}
              </button>
            ))
          )}
        </Card>
      ))}
    </div>
  );
}

/** "Tuesday" for a YYYY-MM-DD. */
const weekdayName = (s) => fromIso(s).toLocaleDateString(undefined, { weekday: "long" });

/** "Sep 22, 2026" for a YYYY-MM-DD. */
const shortDate = (s) => fromIso(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/**
 * The whole term in one card, one row per meeting, the date on the right.
 *
 * No day headings: a term is one sequence, and the row's own date is the
 * heading. The title carries the weekday and time — the class's name is the
 * page's, and repeating it on thirty rows would say nothing thirty times.
 */
function FlatList({ events, emptyText, todayIso }) {
  const sorted = [...events].sort(
    (a, b) => String(a.date).localeCompare(String(b.date)) || minutes(a.time_local) - minutes(b.time_local),
  );
  return (
    <Card padding={0} data-testid="cal-flat-list">
      {sorted.length === 0 ? (
        <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">{emptyText}</div>
      ) : (
        sorted.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={e.onPress}
            disabled={!e.onPress}
            data-testid={`cal-event-${e.id}`}
            className={[
              "flex w-full items-center gap-3 border-b border-[var(--studio-divider)] px-[18px] py-2.5 text-left last:border-b-0 enabled:hover:bg-[var(--studio-interactive)]",
              e.muted ? "opacity-60" : "",
            ].join(" ")}
          >
            <span className="h-8 w-1 shrink-0 rounded-full" style={{ backgroundColor: e.color || "var(--accent-yellow)" }} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-sans text-[15px] text-[var(--text-primary)]">
                {[weekdayName(e.date), [clock(e.time_local), e.timezone].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
              </span>
              {e.subtitle && (
                <span className="block truncate font-sans text-[13px] text-[var(--text-tertiary)]">{e.subtitle}</span>
              )}
            </span>
            <span className="hidden items-center gap-1.5 sm:flex">
              {(e.tags || []).map((t) => (
                <Tag key={t.label} accent={t.accent}>
                  {t.label}
                </Tag>
              ))}
            </span>
            <span
              className={`shrink-0 font-mono text-[13px] ${
                e.date === todayIso ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-tertiary)]"
              }`}
              data-testid={`cal-event-date-${e.id}`}
            >
              {shortDate(e.date)}
            </span>
          </button>
        ))
      )}
    </Card>
  );
}

/** The time grid. One column per day, one row per hour, events placed on it. */
function WeekGrid({ days, todayIso }) {
  const all = days.flatMap((d) => d.events);
  // Bound the grid by what is actually on it, with an hour of air either side.
  // A fixed 8-to-8 wastes half the screen on a studio that teaches evenings.
  const starts = all.map((e) => minutes(e.time_local));
  const ends = all.map((e) => minutes(e.time_local) + (e.duration_minutes || 45));
  const firstHour = all.length ? Math.max(0, Math.floor(Math.min(...starts) / 60) - 1) : 8;
  const lastHour = all.length ? Math.min(24, Math.ceil(Math.max(...ends) / 60) + 1) : 20;
  const hours = Array.from({ length: Math.max(1, lastHour - firstHour) }, (_, i) => firstHour + i);
  const top = (m) => ((m - firstHour * 60) / 60) * ROW;

  return (
    <Card padding={0} className="overflow-x-auto" data-testid="cal-week-grid">
      <div className="min-w-[760px]">
        {/* Day headings */}
        <div className="grid grid-cols-[52px_repeat(7,1fr)] border-b border-[var(--studio-divider)]">
          <div />
          {days.map((d) => {
            const date = fromIso(d.key);
            const isToday = d.key === todayIso;
            return (
              <div key={d.key} className="px-2 py-2 text-center">
                <div className="font-sans text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">
                  {WEEKDAYS[(date.getDay() + 6) % 7]}
                </div>
                <div
                  className={`font-sans text-[15px] font-semibold ${
                    isToday ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-primary)]"
                  }`}
                >
                  {date.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Hour rows + the day columns laid over them */}
        <div className="relative grid grid-cols-[52px_repeat(7,1fr)]">
          <div>
            {hours.map((h) => (
              <div
                key={h}
                style={{ height: ROW }}
                className="border-b border-[var(--studio-divider)] pr-2 text-right font-mono text-[10px] text-[var(--text-tertiary)]"
              >
                {clock(`${String(h).padStart(2, "0")}:00`).replace(":00", "")}
              </div>
            ))}
          </div>
          {days.map((day) => (
            <div key={day.key} className="relative border-l border-[var(--studio-divider)]">
              {hours.map((h) => (
                <div key={h} style={{ height: ROW }} className="border-b border-[var(--studio-divider)]" />
              ))}
              {layoutDay(day.events).map(({ event, start, end, lane, span, lanes }) => (
                <div
                  key={event.id}
                  data-testid={`cal-block-${event.id}`}
                  className="absolute px-0.5"
                  style={{
                    top: top(start),
                    height: Math.max(MIN_BLOCK, ((end - start) / 60) * ROW),
                    left: `${(lane / lanes) * 100}%`,
                    width: `${(span / lanes) * 100}%`,
                  }}
                >
                  <div className="h-full">
                    <EventChip event={event} compact={span / lanes < 0.4} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/** Six weeks of cells. Detail per meeting is traded for the shape of a term. */
function MonthGrid({ weeks, byDay, anchor, todayIso, onOpenDay }) {
  const month = anchor.getMonth();
  const PER_CELL = 3;
  return (
    <Card padding={0} data-testid="cal-month-grid">
      <div className="grid grid-cols-7 border-b border-[var(--studio-divider)]">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-2 py-2 text-center font-sans text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {weeks.flat().map((key) => {
          const events = byDay[key] || [];
          const outside = fromIso(key).getMonth() !== month;
          const isToday = key === todayIso;
          return (
            <div
              key={key}
              data-testid={`cal-month-day-${key}`}
              className={`min-h-[92px] border-b border-l border-[var(--studio-divider)] p-1.5 ${
                outside ? "opacity-45" : ""
              }`}
            >
              <div
                className={`mb-1 font-sans text-[12px] font-semibold ${
                  isToday ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-tertiary)]"
                }`}
              >
                {fromIso(key).getDate()}
              </div>
              <div className="flex flex-col gap-0.5">
                {events.slice(0, PER_CELL).map((e) => (
                  <EventChip key={e.id} event={e} compact />
                ))}
                {events.length > PER_CELL && (
                  <button
                    type="button"
                    onClick={() => onOpenDay?.(key)}
                    data-testid={`cal-month-more-${key}`}
                    className="self-start font-sans text-[11px] text-[var(--text-tertiary)] hover:text-[var(--accent-yellow-text)]"
                  >
                    +{events.length - PER_CELL} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------- the control */

/**
 * @param {{
 *   events: Array, view: string, onViewChange: Function,
 *   anchor: Date, onAnchorChange: Function, loading?: boolean,
 *   emptyText?: string, onOpenDay?: Function, listStyle?: "grouped"|"flat",
 * }} props
 */
export default function MeetingCalendar({
  events = [],
  view,
  onViewChange,
  anchor,
  onAnchorChange,
  loading = false,
  emptyText = "Nothing scheduled",
  onOpenDay,
  listStyle = "grouped",
}) {
  const todayIso = iso(new Date());
  // A flat list is the whole term, so there is no week to page through.
  const flat = listStyle === "flat" && view === "list";

  const byDay = useMemo(() => {
    const map = {};
    for (const e of events) (map[e.date] ||= []).push(e);
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => minutes(a.time_local) - minutes(b.time_local));
    }
    return map;
  }, [events]);

  const days = useMemo(() => {
    const first = weekStart(anchor);
    return Array.from({ length: 7 }, (_, i) => {
      const key = iso(addDays(first, i));
      return { key, events: byDay[key] || [] };
    });
  }, [anchor, byDay]);

  const weeks = useMemo(() => {
    const first = monthGridStart(anchor);
    return Array.from({ length: 6 }, (_, w) =>
      Array.from({ length: 7 }, (_, d) => iso(addDays(first, w * 7 + d))),
    );
  }, [anchor]);

  const move = (delta) =>
    onAnchorChange(
      view === "month"
        ? new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1)
        : addDays(anchor, delta * 7),
    );

  const label =
    view === "month"
      ? anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
      : `${weekStart(anchor).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(
          weekStart(anchor),
          6,
        ).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {flat ? (
          <span className="font-sans text-[14px] text-[var(--text-secondary)]" data-testid="cal-range">
            {events.length} {events.length === 1 ? "meeting" : "meetings"} this term
          </span>
        ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => move(-1)}
            aria-label={view === "month" ? "Previous month" : "Previous week"}
            data-testid="cal-prev"
            className="rounded-full border border-[var(--studio-divider-strong)] p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <Icon name="chevronLeft" size={16} />
          </button>
          <button
            type="button"
            onClick={() => onAnchorChange(new Date())}
            data-testid="cal-today"
            className="rounded-full border border-[var(--studio-divider-strong)] px-3 py-1.5 font-sans text-[14px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => move(1)}
            aria-label={view === "month" ? "Next month" : "Next week"}
            data-testid="cal-next"
            className="rounded-full border border-[var(--studio-divider-strong)] p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <Icon name="chevronRight" size={16} />
          </button>
          <span className="ml-1 font-sans text-[14px] text-[var(--text-secondary)]" data-testid="cal-range">
            {label}
          </span>
        </div>
        )}
        <SegmentedControl testId="cal-views" value={view} onChange={onViewChange} options={VIEWS} />
      </div>

      {loading ? (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
      ) : flat ? (
        <FlatList events={events} emptyText={emptyText} todayIso={todayIso} />
      ) : view === "list" ? (
        <ListView days={days} emptyText={emptyText} />
      ) : view === "week" ? (
        <WeekGrid days={days} todayIso={todayIso} />
      ) : (
        <MonthGrid weeks={weeks} byDay={byDay} anchor={anchor} todayIso={todayIso} onOpenDay={onOpenDay} />
      )}
    </div>
  );
}
